/**
 * QUE PLANTILLA DE RECORDATORIO DE PAGO TOCA.
 *
 * Lissette, 2026-09-01 (punto 7 de 9): en Pagos del Mes quiere *"más de dos
 * plantillas de recordatorio por compañía (1 mes vs 60 días)"*.
 *
 * ── LO QUE FALTABA, Y NO ERA LA PANTALLA ───────────────────────────────────
 *
 * `reminder_templates` tenía `UNIQUE (agency_id, policy_type, carrier)`: UNA
 * sola plantilla por compañía y tipo. Guardar una segunda para la misma
 * compañía sobreescribía la primera. No es que la pantalla no lo ofreciera —
 * la base de datos lo prohibía.
 *
 * Y lo que faltaba modelar es POR QUÉ querría dos: el mensaje no depende solo
 * de la compañía, depende de **cuánto lleva sin pagarse**. El primer aviso del
 * mes en curso y el de alguien que lleva dos meses no se escriben igual.
 *
 * ── COMO SE ELIGE ──────────────────────────────────────────────────────────
 *
 * Dos ejes, y el orden entre ellos importa:
 *
 *   1. EL ATRASO manda dentro de cada nivel: se coge la plantilla con el
 *      `monthsOverdue` MÁS ALTO que no pase del atraso real. Con plantillas en
 *      0 y 2, un pago de un mes usa la de 0 y uno de tres usa la de 2 — así
 *      nunca queda un hueco sin mensaje.
 *   2. LA ESPECIFICIDAD manda entre niveles: compañía exacta → tipo de póliza →
 *      global. Es la escalera que ya existía y no se toca.
 *
 * El orden es ése y no al revés: una plantilla escrita PARA esa compañía gana
 * a una global aunque la global tenga un atraso mejor ajustado. Al revés, la
 * agente escribiría una plantilla para Ambetter y vería salir la genérica.
 *
 * Vive fuera de `PoliciesView.jsx` para poder ejecutarlo en las pruebas sin
 * montar React ni arrastrar MSAL.
 */

/**
 * Cuántos meses de atraso lleva el pago de `periodo` ("YYYY-MM") a fecha de
 * `hoy`. 0 = el mes en curso o el futuro; nunca negativo.
 *
 * Se cuenta por MESES DE CALENDARIO, no por días: la vista es "Pagos del Mes"
 * y un pago pertenece a un mes entero, no a un día. Contar días haría que el
 * mismo recibo cambiara de plantilla a mitad de mes.
 */
export function mesesDeAtraso(periodo, hoy = new Date()) {
  const m = /^(\d{4})-(\d{2})$/.exec(String(periodo || '').trim());
  if (!m) return 0;
  const anio = Number(m[1]);
  const mes = Number(m[2]);
  if (!Number.isFinite(anio) || !Number.isFinite(mes) || mes < 1 || mes > 12) return 0;
  // Mes local, no UTC: en Florida por la noche el mes UTC ya puede ser el
  // siguiente, y el recibo saltaría de plantilla por el huso horario.
  const diff = (hoy.getFullYear() - anio) * 12 + (hoy.getMonth() + 1 - mes);
  return diff > 0 ? diff : 0;
}

/** El cuerpo en el idioma pedido, cayendo al otro si ese está vacío. */
function cuerpo(t, es) {
  return (es ? (t.bodyEs || t.body) : (t.body || t.bodyEs)) || '';
}

const atrasoDe = (t) => {
  const n = Number(t?.monthsOverdue);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

/**
 * De una lista ya filtrada a un nivel de especificidad, la plantilla que mejor
 * encaja con `atraso`: el `monthsOverdue` más alto que no lo pase. Ignora las
 * que no tienen texto — una plantilla vacía no es una respuesta.
 */
function mejorPorAtraso(lista, atraso, es) {
  let mejor = null;
  for (const t of lista) {
    const n = atrasoDe(t);
    if (n > atraso) continue;
    if (!cuerpo(t, es).trim()) continue;
    if (!mejor || n > atrasoDe(mejor)) mejor = t;
  }
  return mejor;
}

/**
 * La plantilla para (tipo, compañía, atraso). Devuelve el TEXTO, o '' si no hay
 * ninguna — el modal cae entonces a su plantilla de fábrica.
 */
export function elegirPlantilla(plantillas, { tipo, compania, atraso = 0, es = true }) {
  const lista = Array.isArray(plantillas) ? plantillas : [];
  const niveles = [
    lista.filter((t) => t.policyType === tipo && t.carrier === compania),
    lista.filter((t) => t.policyType === tipo && !t.carrier),
    lista.filter((t) => !t.policyType && !t.carrier),
  ];
  for (const nivel of niveles) {
    const hit = mejorPorAtraso(nivel, atraso, es);
    if (hit) return cuerpo(hit, es);
  }
  return '';
}

/**
 * Las etapas que la agente tiene guardadas para (tipo, compañía), de menos a
 * más atraso, para poder ELEGIR otra a mano en el modal. Incluye las heredadas
 * de los niveles menos específicos: si tiene una global a 60 días, esa etapa
 * también está disponible aquí.
 */
export function etapasDisponibles(plantillas, { tipo, compania, es = true }) {
  const lista = Array.isArray(plantillas) ? plantillas : [];
  const aplica = (t) =>
    (t.policyType === tipo && t.carrier === compania)
    || (t.policyType === tipo && !t.carrier)
    || (!t.policyType && !t.carrier);
  const vistos = new Set([0]);
  for (const t of lista) {
    if (aplica(t) && cuerpo(t, es).trim()) vistos.add(atrasoDe(t));
  }
  return [...vistos].sort((a, b) => a - b);
}

/** Como se lee una etapa en pantalla. */
export function nombreDeEtapa(meses, lang = 'es') {
  const n = Number(meses) || 0;
  if (lang === 'es') {
    if (n <= 0) return 'Aviso normal';
    if (n === 1) return '1 mes de atraso';
    return `${n} meses de atraso`;
  }
  if (n <= 0) return 'Standard reminder';
  if (n === 1) return '1 month overdue';
  return `${n} months overdue`;
}

/**
 * ── LAS ETIQUETAS QUE SE RELLENAN SOLAS ────────────────────────────────────
 *
 * Las mismas en los dos idiomas a propósito: una plantilla guardada en español
 * tiene que seguir funcionando si la agente cambia la pantalla a inglés.
 *
 * `{prima}` la pidió Lissette el 2026-09-12: *"en el recordatorio de pago
 * permitir en personalizar el mensaje poder colocar predeterminado la prima de
 * la póliza"*. Llega ya formateada como dinero ($1.234,56 → `$1,234.56`), no
 * como número suelto: quien escribe la plantilla no debería tener que pensar en
 * el formato, y un "su prima es 1234.5" en un mensaje al cliente canta.
 */
export const ETIQUETAS_DE_RECORDATORIO = ['{nombre}', '{compania}', '{tipo}', '{mes}', '{poliza}', '{prima}'];

/**
 * Sustituye las etiquetas por los datos de ese cliente/póliza.
 *
 * Lo delicado no es sustituir, es lo que queda cuando un dato FALTA. Una póliza
 * sin número dejaba "(póliza #)" colgando en mitad de la frase, y por eso la
 * plantilla de fábrica escribe tanto el número como la prima ENTRE PARÉNTESIS:
 * es lo que permite borrar la frase entera sin adivinar dónde empieza y dónde
 * acaba. Un borrado genérico ("quita el 'de' que quede suelto") se llevaría por
 * delante cualquier frase que acabe en "de".
 */
export function rellenarRecordatorio(plantilla, datos = {}) {
  const { firstName, company, typeLabel, monthLabel, memberNo, premium } = datos;
  let s = String(plantilla || '')
    .replace(/\{nombre\}/g, firstName || '')
    .replace(/\{compania\}/g, company || '')
    .replace(/\{tipo\}/g, typeLabel || '')
    .replace(/\{mes\}/g, monthLabel || '')
    .replace(/\{poliza\}/g, memberNo || '')
    .replace(/\{prima\}/g, premium || '');
  if (!memberNo) s = s.replace(/\s*\((?:p[oó]liza|policy)\s*#?\s*\)/gi, '');
  if (!premium) s = s.replace(/\s*\((?:prima|premium)\s*:?\s*\)/gi, '');
  // Solo espacios y tabuladores: `\s` se llevaría también el salto de línea, y
  // una plantilla de varias líneas acabaría pegada en una sola.
  return s.replace(/[ \t]{2,}/g, ' ').replace(/[ \t]+([.,;:])/g, '$1').trim();
}

/** La plantilla de fábrica, la que sale cuando no hay ninguna guardada. */
export function plantillaDeFabrica(es) {
  return es
    ? 'Hola {nombre}, le recordamos que el pago de su póliza de {tipo} con {compania} (póliza #{poliza}) correspondiente a {mes} (prima: {prima}) está pendiente. Por favor realice el pago para mantener su cobertura activa. ¡Gracias!'
    : 'Hi {nombre}, this is a friendly reminder that your {tipo} policy payment with {compania} (policy #{poliza}) for {mes} (premium: {prima}) is still pending. Please make the payment to keep your coverage active. Thank you!';
}
